import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { signOut } from "firebase/auth";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { auth, db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";
import "./dashboard.css";
import prufyyLogo from "../../assets/prufyy-logo-white.png";

const ONBOARDING_KEY = "prufyy_founder_onboarding_seen";

function DashboardLayout() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);

  const [likeCount, setLikeCount] = useState(0);
  const [lastSeenAt, setLastSeenAt] = useState(null);

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);

  const isPaid = plan === "paid";

  /* =========================
     LOAD USER PLAN + LAST SEEN
     ========================= */
  useEffect(() => {
    if (!user?.uid) return;

    const loadUserMeta = async () => {
      try {
        const refDoc = doc(db, "users", user.uid);
        const snap = await getDoc(refDoc);

        if (snap.exists()) {
          const data = snap.data();
          setPlan(data.plan || "free");
          setLastSeenAt(data.progressFeedLastSeenAt || null);
        } else {
          setPlan("free");
        }
      } catch (err) {
        console.error("Failed to load user meta", err);
        setPlan("free");
      } finally {
        setPlanLoading(false);
      }
    };

    loadUserMeta();
  }, [user?.uid]);

  /* =========================
     FIRST-TIME ONBOARDING TRIGGER
     ========================= */
  useEffect(() => {
    if (!planLoading && user?.uid) {
      const seen = localStorage.getItem(ONBOARDING_KEY);
      if (!seen) {
        setShowOnboarding(true);
      }
    }
  }, [planLoading, user?.uid]);

  /* =========================
     REALTIME UNREAD LIKE COUNT (PAID ONLY)
     ========================= */
  useEffect(() => {
    if (!user?.uid || !isPaid) return;

    const q = query(
      collection(db, "progressPosts"),
      where("founderId", "==", user.uid)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      let unread = 0;

      snap.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const likesCount = data.likesCount || 0;
        const lastLikeAt = data.lastLikeAt;

        if (
          likesCount > 0 &&
          (!lastSeenAt ||
            (lastLikeAt &&
              lastLikeAt.toMillis() > lastSeenAt.toMillis()))
        ) {
          unread += likesCount;
        }
      });

      setLikeCount(unread);
    });

    return () => unsubscribe();
  }, [user?.uid, lastSeenAt, isPaid]);

  /* =========================
     MARK FEED AS SEEN (PAID ONLY)
     ========================= */
  const handleProgressFeedClick = async () => {
    if (!user?.uid || !isPaid) return;

    try {
      await updateDoc(doc(db, "users", user.uid), {
        progressFeedLastSeenAt: Timestamp.now(),
      });

      setLikeCount(0);
      setLastSeenAt(Timestamp.now());
    } catch (err) {
      console.error("Failed to mark feed as seen", err);
    }
  };

  /* =========================
     LOGOUT
     ========================= */
  const handleLogout = () => {
    navigate("/logout");
  };

  /* =========================
     UPGRADE HANDLER
     ========================= */
  const handleUpgradeClick = (featureName) => {
    alert(
      `${featureName} is available on the Paid plan. Upgrade to unlock advanced credibility insights.`
    );
  };

  /* =========================
     ONBOARDING CONTENT
     ========================= */
  const onboardingSteps = [
    {
      title: "Welcome to Prufyy",
      text:
        "This dashboard is where you build and present your startup’s credibility. We’ll show you how everything fits together.",
    },
    {
      title: "Start with your Profile",
      text:
        "Your profile gives evaluators essential context before they review your claims. Completing it improves clarity and trust.",
    },
    {
      title: "Pitch → Claims → Evidence",
      text:
        "Upload your pitch, review AI-generated claims, and attach strong evidence. This is the core credibility flow.",
    },
    {
      title: "Locked features",
      text:
        "Some insights unlock on the Paid plan. Free users can still build credibility — upgrades add deeper analytics and feedback.",
    },
    {
      title: "You’re ready",
      text:
        "You can restart this guide anytime using the Help button. Build clearly, and let evaluators judge responsibly.",
    },
  ];

  const closeOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setShowOnboarding(false);
    setOnboardingStep(0);
  };

  return (
    <div className="dashboard">
      {/* ================= ONBOARDING OVERLAY ================= */}
      {showOnboarding && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.65)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            style={{
              maxWidth: 520,
              width: "100%",
              background: "#fff",
              borderRadius: 16,
              padding: 28,
              boxShadow: "0 30px 80px rgba(0,0,0,0.3)",
            }}
          >
            <h2 style={{ marginBottom: 12 }}>
              {onboardingSteps[onboardingStep].title}
            </h2>
            <p style={{ color: "#475569", lineHeight: 1.6 }}>
              {onboardingSteps[onboardingStep].text}
            </p>

            <div
              style={{
                marginTop: 24,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                className="btn-secondary"
                onClick={closeOnboarding}
              >
                Skip
              </button>

              <div style={{ display: "flex", gap: 8 }}>
                {onboardingStep < onboardingSteps.length - 1 ? (
                  <button
                    className="btn-primary"
                    onClick={() =>
                      setOnboardingStep((s) => s + 1)
                    }
                  >
                    Next
                  </button>
                ) : (
                  <button
                    className="btn-primary"
                    onClick={closeOnboarding}
                  >
                    Finish
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= SIDEBAR ================= */}
      <aside className="sidebar">
        <div className="sidebar-logo">
          <img
            src={prufyyLogo}
            alt="prufyy"
            className="sidebar-logo-image"
          />
        </div>

        <nav className="sidebar-nav">
          {planLoading ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>
              Loading features…
            </div>
          ) : (
            <>
              <NavLink to="/dashboard/profile">Profile</NavLink>
              <NavLink to="/dashboard/pitch">Pitch</NavLink>
              <NavLink to="/dashboard/claims">Claims</NavLink>
              <NavLink to="/dashboard/startup-records">
                Startup Records
              </NavLink>

              {isPaid ? (
                <NavLink
                  to="/dashboard/progress-feed"
                  onClick={handleProgressFeedClick}
                >
                  <span style={{ display: "flex", gap: 8 }}>
                    Progress Feed
                    {likeCount > 0 && (
                      <span
                        style={{
                          background: "#2563eb",
                          color: "#fff",
                          fontSize: 11,
                          padding: "2px 6px",
                          borderRadius: 999,
                        }}
                      >
                        {likeCount}
                      </span>
                    )}
                  </span>
                </NavLink>
              ) : (
                <div
                  className="sidebar-locked"
                  onClick={() =>
                    handleUpgradeClick("Progress Feed")
                  }
                  title="Upgrade to track evaluator engagement"
                >
                  Progress Feed 🔒
                </div>
              )}

              {isPaid ? (
                <NavLink to="/dashboard/analytics">
                  Analytics
                </NavLink>
              ) : (
                <div
                  className="sidebar-locked"
                  onClick={() =>
                    handleUpgradeClick("Analytics")
                  }
                  title="Analytics unlock deeper credibility insights"
                >
                  Analytics 🔒
                </div>
              )}

              {isPaid ? (
                <NavLink to="/dashboard/credibility-feedback">
                  Credibility Feedback
                </NavLink>
              ) : (
                <div
                  className="sidebar-locked"
                  onClick={() =>
                    handleUpgradeClick(
                      "Credibility Feedback"
                    )
                  }
                  title="View evaluator feedback with a Paid plan"
                >
                  Credibility Feedback 🔒
                </div>
              )}
            </>
          )}
        </nav>
      </aside>

      {/* ================= MAIN ================= */}
      <div className="dashboard-main">
        <header
          className="dashboard-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            className="btn-secondary"
            onClick={() => {
              setShowOnboarding(true);
              setOnboardingStep(0);
            }}
          >
            Help
          </button>

          <button
            className="logout-button"
            onClick={handleLogout}
          >
            Logout
          </button>
        </header>

        <main className="dashboard-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default DashboardLayout;
